/**
 * Dynamic OG Image Generator for Social Media Previews
 * Generates images with titles overlaid for Facebook, LinkedIn, and Twitter
 */

export interface OGImageConfig {
  title: string
  description?: string
  author?: string
  type?: 'blog' | 'meme' | 'webinar' | 'topic' | 'course' | 'profile'
  imageUrl?: string
  logo?: string
  backgroundColor?: string
  textColor?: string
  accentColor?: string
}

export interface OGImageData {
  url: string
  width: number
  height: number
  alt: string
}

const DEFAULT_CONFIG = {
  width: 1200,
  height: 630,
  backgroundColor: '#1f2937',
  textColor: '#ffffff',
  accentColor: '#3b82f6',
  logo: 'https://fullstackdatascience.com/favicon.ico',
  fontFamily: 'Inter, system-ui, sans-serif',
}

/**
 * Generate a dynamic OG image URL with title overlaid
 * Uses a service like Vercel OG Image or similar
 */
export function generateOGImage(config: OGImageConfig): OGImageData {
  const {
    title,
    description,
    author,
    type = 'blog',
    imageUrl,
    logo = DEFAULT_CONFIG.logo,
    backgroundColor = DEFAULT_CONFIG.backgroundColor,
    textColor = DEFAULT_CONFIG.textColor,
    accentColor = DEFAULT_CONFIG.accentColor,
  } = config

  // Base URL for OG image generation service
  // Use the local API endpoint for OG image generation
  const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://fullstackdatascience.com'

  // Encode parameters
  const params = new URLSearchParams({
    title: title.substring(0, 60), // Limit title length
    description: description ? description.substring(0, 120) : '',
    author: author || 'FSDS',
    type,
    backgroundColor: backgroundColor.replace('#', ''),
    textColor: textColor.replace('#', ''),
    accentColor: accentColor.replace('#', ''),
    logo: encodeURIComponent(logo),
    ...(imageUrl && { imageUrl: encodeURIComponent(imageUrl) }),
  })

  const ogImageUrl = `${baseUrl}/api/og?${params.toString()}`

  return {
    url: ogImageUrl,
    width: DEFAULT_CONFIG.width,
    height: DEFAULT_CONFIG.height,
    alt: `${title} - ${description || 'FSDS Content'}`,
  }
}

/**
 * Generate OG image for blog posts
 */
export function generateBlogOGImage(blog: {
  title: string
  metaDescription?: string
  content?: string
  author?: { firstName?: string; lastName?: string }
  thumbnailImage?: string
}): OGImageData {
  const authorName = blog.author ? `${blog.author.firstName || ''} ${blog.author.lastName || ''}`.trim() : 'FSDS Team'

  return generateOGImage({
    title: blog.title,
    description: blog.metaDescription || blog.content?.substring(0, 120),
    author: authorName,
    type: 'blog',
    imageUrl: blog.thumbnailImage,
  })
}

/**
 * Generate OG image for meme posts
 */
export function generateMemeOGImage(meme: {
  content: string
  user: { firstName?: string; lastName?: string }
  images?: Array<{ imageUrl: string }>
}): OGImageData {
  const userName = `${meme.user.firstName || ''} ${meme.user.lastName || ''}`.trim()
  const firstImage = meme.images?.[0]?.imageUrl

  return generateOGImage({
    title: `Meme by ${userName}`,
    description: meme.content.substring(0, 120),
    author: userName,
    type: 'meme',
    imageUrl: firstImage,
    backgroundColor: '#dc2626', // Red background for memes
    accentColor: '#f97316', // Orange accent
  })
}

/**
 * Generate OG image for webinars
 */
export function generateWebinarOGImage(webinar: {
  title: string
  description?: string
  author?: { firstName?: string; lastName?: string }
  thumbnailImage?: string
}): OGImageData {
  const authorName = webinar.author
    ? `${webinar.author.firstName || ''} ${webinar.author.lastName || ''}`.trim()
    : 'FSDS Team'

  return generateOGImage({
    title: webinar.title,
    description: webinar.description,
    author: authorName,
    type: 'webinar',
    imageUrl: webinar.thumbnailImage,
    backgroundColor: '#059669', // Green background for educational content
    accentColor: '#10b981',
  })
}

/**
 * Generate OG image for topics/discussions
 */
export function generateTopicOGImage(topic: {
  title: string
  content?: string
  user: { firstName?: string; lastName?: string }
  isForum?: boolean
}): OGImageData {
  const userName = `${topic.user.firstName || ''} ${topic.user.lastName || ''}`.trim()

  return generateOGImage({
    title: topic.title,
    description: topic.content?.substring(0, 120),
    author: userName,
    type: topic.isForum ? 'topic' : 'course',
    backgroundColor: '#7c3aed', // Purple background for discussions
    accentColor: '#a855f7',
  })
}

/**
 * Generate OG image for courses
 */
export function generateCourseOGImage(course: {
  title: string
  description?: string
  thumbnailImage?: string
}): OGImageData {
  return generateOGImage({
    title: course.title,
    description: course.description,
    author: 'FSDS Team',
    type: 'course',
    imageUrl: course.thumbnailImage,
    backgroundColor: '#1e40af', // Blue background for courses
    accentColor: '#3b82f6',
  })
}

/**
 * Generate OG image for user profiles
 */
export function generateProfileOGImage(user: {
  firstName?: string
  lastName?: string
  avatar?: string
  bio?: string
}): OGImageData {
  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim()

  return generateOGImage({
    title: `${fullName}'s Profile`,
    description: user.bio?.substring(0, 120) || 'FSDS Community Member',
    author: fullName,
    type: 'profile',
    imageUrl: user.avatar,
    backgroundColor: '#374151', // Gray background for profiles
    accentColor: '#6b7280',
  })
}

/**
 * Generate embed URL for shared content
 */
export function generateEmbedUrl(contentType: string, contentId: string): string {
  const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://fullstackdatascience.com'
  return `${baseUrl}/embed/${contentType}/${contentId}`
}

/**
 * Generate social share URL with embed parameters
 */
export function generateSocialShareUrl(
  contentType: string,
  contentId: string,
  platform: 'facebook' | 'linkedin' | 'twitter'
): string {
  const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://fullstackdatascience.com'
  const shareUrl = `${baseUrl}/${contentType}/${contentId}`
  const embedUrl = generateEmbedUrl(contentType, contentId)

  switch (platform) {
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(embedUrl)}`
    case 'linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(embedUrl)}`
    case 'twitter':
      return `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(embedUrl)}`
    default:
      return shareUrl
  }
}
